package com.castly.admin.ui.activity

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.ActiveDownloadsResponse
import com.castly.shared.dto.ImportHistoryResponse
import com.castly.shared.dto.ImportLogSummaryDto
import com.castly.shared.dto.ImportLogDetailDto
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private fun typeLabel(importType: String): String = when (importType) {
    "CSV_AUDIOBOOK" -> "CSV Import"
    "FOLDER_EPISODE" -> "Episode Import"
    "ONIX_AUDIOBOOK" -> "ONIX Import"
    "RSS_AUDIOBOOK" -> "RSS Import"
    "PODCAST_CONVERSION" -> "Podcast Convert"
    "EPISODE_UPLOAD" -> "Episode Upload"
    "BULK_EPISODE" -> "Bulk Import"
    else -> importType
}

private fun contentLabel(contentType: String?): String = when (contentType) {
    "audiobook" -> "Audiobook"
    "podcast" -> "Podcast"
    else -> ""
}

private fun statusColor(status: String, colors: com.castly.admin.ui.theme.AppColors): String = when (status) {
    "SUCCESS" -> colors.success
    "PARTIAL" -> "#f59e0b"
    "FAILED" -> colors.danger
    else -> colors.textMuted
}

private fun relativeTime(isoTimestamp: String): String {
    return try {
        val ts = kotlin.js.Date(isoTimestamp.replace(" ", "T"))
        val now = kotlin.js.Date()
        val diffMs = now.getTime() - ts.getTime()
        val diffMin = (diffMs / 60000).toInt()
        val diffHour = (diffMs / 3600000).toInt()
        val diffDay = (diffMs / 86400000).toInt()
        when {
            diffMin < 1 -> "just now"
            diffMin < 60 -> "${diffMin}m ago"
            diffHour < 24 -> "${diffHour}h ago"
            diffDay < 7 -> "${diffDay}d ago"
            else -> isoTimestamp.take(10)
        }
    } catch (_: Exception) {
        isoTimestamp.take(16).replace("T", " ")
    }
}

@Composable
fun ActivityPage(apiClient: ApiClient) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var downloads by remember { mutableStateOf<ActiveDownloadsResponse?>(null) }
    var history by remember { mutableStateOf<ImportHistoryResponse?>(null) }
    var currentPage by remember { mutableStateOf(0) }
    var expandedLogId by remember { mutableStateOf<Int?>(null) }
    var expandedDetail by remember { mutableStateOf<ImportLogDetailDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var typeFilter by remember { mutableStateOf<String?>(null) }
    var contentFilter by remember { mutableStateOf<String?>(null) }

    val pageSize = 20

    // Poll active downloads
    LaunchedEffect(Unit) {
        while (isActive) {
            try { downloads = apiClient.getActiveDownloads() } catch (_: Exception) {}
            delay(5000)
        }
    }

    // Load history
    LaunchedEffect(currentPage) {
        loading = true
        try { history = apiClient.getImportHistory(limit = pageSize, offset = currentPage * pageSize) } catch (_: Exception) {}
        loading = false
    }

    Div({
        style {
            padding(Spacing.xl)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.lg)
            maxWidth(900.px)
            themeTransition()
        }
    }) {
        // Header
        H2({
            style {
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
                margin(0.px)
            }
        }) { Text("Activity") }

        P({
            style {
                fontSize(Typography.sm)
                color(Color(colors.textMuted))
                margin(0.px)
                marginBottom(Spacing.md)
            }
        }) { Text("Upload history and active tasks across all content types.") }

        // Filter pills
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.sm)
                flexWrap(FlexWrap.Wrap)
                marginBottom(Spacing.md)
            }
        }) {
            // Content type filters
            listOf(null to "All", "audiobook" to "Audiobook", "podcast" to "Podcast").forEach { (value, label) ->
                FilterPill(label, contentFilter == value) {
                    contentFilter = value
                }
            }

            Span({ style { width(16.px) } })

            // Type filters
            listOf(
                null to "All Types",
                "EPISODE_UPLOAD" to "Upload",
                "FOLDER_EPISODE" to "Episode Import",
                "BULK_EPISODE" to "Bulk Import",
                "CSV_AUDIOBOOK" to "CSV",
                "ONIX_AUDIOBOOK" to "ONIX",
                "RSS_AUDIOBOOK" to "RSS"
            ).forEach { (value, label) ->
                FilterPill(label, typeFilter == value) {
                    typeFilter = value
                }
            }
        }

        // Active tasks section
        val activeDownloads = downloads
        val hasActive = activeDownloads != null && activeDownloads.inProgress.isNotEmpty()
        if (hasActive) {
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#eff6ff" else "#0c1929"))
                    borderRadius(BorderRadius.lg)
                    padding(Spacing.lg)
                    property("border", "1px solid ${if (colors == LightColors) "#bfdbfe" else "#1e3a5f"}")
                }
            }) {
                H3({
                    style {
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(colors.text))
                        margin(0.px)
                        marginBottom(Spacing.md)
                    }
                }) { Text("Active Tasks") }

                activeDownloads!!.inProgress.forEach { download ->
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.md)
                            padding(Spacing.sm, 0.px)
                        }
                    }) {
                        // Spinner
                        Style { "@keyframes activity-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }" }
                        Span({
                            style {
                                display(DisplayStyle.InlineBlock)
                                property("animation", "activity-spin 1s linear infinite")
                                fontSize(Typography.base)
                            }
                        }) { Text("\u21BB") }

                        Div({ style { flex(1) } }) {
                            Div({
                                style {
                                    fontSize(Typography.sm)
                                    fontWeight("500")
                                    color(Color(colors.text))
                                }
                            }) { Text(download.audiobookTitle) }
                            Div({
                                style {
                                    fontSize(Typography.xs)
                                    color(Color(colors.textMuted))
                                }
                            }) {
                                Text("${download.completedChapters}/${download.totalChapters} chapters")
                                if (download.failedChapters > 0) {
                                    Text(" (${download.failedChapters} failed)")
                                }
                            }
                        }

                        // Progress bar
                        Div({
                            style {
                                width(120.px)
                                height(6.px)
                                backgroundColor(Color(colors.border))
                                borderRadius(3.px)
                                overflow("hidden")
                            }
                        }) {
                            val pct = if (download.totalChapters > 0) (download.completedChapters.toDouble() / download.totalChapters * 100).toInt() else 0
                            Div({
                                style {
                                    height(100.percent)
                                    width(pct.percent)
                                    backgroundColor(Color(colors.primary))
                                    property("transition", "width 0.3s")
                                }
                            })
                        }
                    }
                }
            }
        }

        // History section
        Div({
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                padding(Spacing.lg)
                border(1.px, LineStyle.Solid, Color(colors.border))
            }
        }) {
            H3({
                style {
                    fontSize(Typography.base)
                    fontWeight("600")
                    color(Color(colors.text))
                    margin(0.px)
                    marginBottom(Spacing.lg)
                }
            }) { Text("History") }

            val items = history?.items?.filter { log ->
                (contentFilter == null || log.contentType == contentFilter) &&
                (typeFilter == null || log.importType == typeFilter)
            } ?: emptyList()

            if (loading) {
                Div({
                    style {
                        padding(Spacing.xl)
                        textAlign("center")
                        color(Color(colors.textMuted))
                    }
                }) { Text("Loading...") }
            } else if (items.isEmpty()) {
                Div({
                    style {
                        padding(Spacing.xl)
                        textAlign("center")
                        color(Color(colors.textMuted))
                    }
                }) { Text("No activity yet.") }
            } else {
                items.forEach { log ->
                    val isExpanded = expandedLogId == log.id

                    // Row
                    Div({
                        onClick {
                            if (isExpanded) {
                                expandedLogId = null
                                expandedDetail = null
                            } else {
                                expandedLogId = log.id
                                scope.launch {
                                    try { expandedDetail = apiClient.getImportLogDetail(log.id) } catch (_: Exception) {}
                                }
                            }
                        }
                        style {
                            padding(Spacing.md)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            property("transition", "background-color 0.15s")
                            if (isExpanded) {
                                backgroundColor(Color(colors.surfaceHover))
                            }
                        }
                    }) {
                        // Main row
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(Spacing.sm)
                            }
                        }) {
                            // Status indicator
                            Span({
                                style {
                                    fontSize(Typography.base)
                                    property("min-width", "20px")
                                }
                            }) {
                                Text(when (log.status) {
                                    "SUCCESS" -> "\u2713"
                                    "PARTIAL" -> "\u26A0"
                                    "FAILED" -> "\u2717"
                                    else -> "\u2014"
                                })
                            }

                            // Type badge
                            Badge(typeLabel(log.importType), colors.primary, if (colors == LightColors) "#eff6ff" else "#0c1929")

                            // Content type badge
                            if (!log.contentType.isNullOrBlank()) {
                                Badge(contentLabel(log.contentType), colors.textMuted, colors.surfaceHover)
                            }

                            // Source badge
                            if (log.source == "background") {
                                Badge("Background", "#f59e0b", if (colors == LightColors) "#fef9c3" else "#451a03")
                            }

                            // Entity name
                            if (!log.entityName.isNullOrBlank()) {
                                Span({
                                    style {
                                        fontWeight("500")
                                        fontSize(Typography.sm)
                                        color(Color(colors.text))
                                    }
                                }) { Text(log.entityName!!) }
                            }

                            Div({ style { flex(1) } })

                            // Time
                            Span({
                                style {
                                    fontSize(Typography.xs)
                                    color(Color(colors.textMuted))
                                    property("white-space", "nowrap")
                                }
                            }) { Text(relativeTime(log.createdAt)) }
                        }

                        // Stats line
                        Div({
                            style {
                                marginTop(4.px)
                                marginLeft(28.px)
                                fontSize(Typography.xs)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            Text("${log.totalRows} files")
                            if (log.successfulRows > 0) {
                                Span({ style { color(Color(colors.success)) } }) {
                                    Text(" \u00B7 ${log.successfulRows} succeeded")
                                }
                            }
                            if (log.failedRows > 0) {
                                Span({ style { color(Color(colors.danger)) } }) {
                                    Text(" \u00B7 ${log.failedRows} failed")
                                }
                            }
                        }

                        // Expanded detail
                        if (isExpanded && expandedDetail != null && expandedDetail!!.id == log.id) {
                            val detail = expandedDetail!!
                            if (!detail.errorReport.isNullOrBlank() && detail.errorReport != "[]") {
                                // Parse errors outside composable (try-catch not allowed around composables)
                                val parsedErrors: List<com.castly.shared.dto.ImportErrorDto>? = try {
                                    kotlinx.serialization.json.Json.decodeFromString(detail.errorReport!!)
                                } catch (_: Exception) { null }

                                Div({
                                    style {
                                        marginTop(Spacing.sm)
                                        marginLeft(28.px)
                                        backgroundColor(Color(if (colors == LightColors) "#fef2f2" else "#450a0a"))
                                        borderRadius(BorderRadius.sm)
                                        padding(Spacing.sm, Spacing.md)
                                        fontSize(12.px)
                                        color(Color(colors.danger))
                                        maxHeight(200.px)
                                        overflowY("auto")
                                        fontFamily("monospace")
                                        property("white-space", "pre-wrap")
                                        property("word-break", "break-word")
                                    }
                                }) {
                                    if (parsedErrors != null) {
                                        parsedErrors.forEach { err ->
                                            Div({ style { marginBottom(4.px) } }) {
                                                val prefix = err.filename ?: err.row?.toString() ?: ""
                                                Text(if (prefix.isNotBlank()) "$prefix: ${err.error}" else err.error)
                                            }
                                        }
                                    } else {
                                        Text(detail.errorReport!!)
                                    }
                                }
                            }
                        }
                    }

                    // Divider
                    if (log != items.last()) {
                        Hr({
                            style {
                                property("border", "none")
                                property("border-top", "1px solid ${colors.border}")
                                margin(0.px)
                            }
                        })
                    }
                }
            }

            // Pagination
            val totalPages = ((history?.total ?: 0) + pageSize - 1) / pageSize
            if (totalPages > 1) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(Spacing.md)
                        marginTop(Spacing.lg)
                        paddingTop(Spacing.md)
                        property("border-top", "1px solid ${colors.border}")
                    }
                }) {
                    Button({
                        onClick { if (currentPage > 0) currentPage-- }
                        if (currentPage == 0) attr("disabled", "")
                        style {
                            padding(6.px, 12.px)
                            backgroundColor(Color(colors.surface))
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            cursor(if (currentPage > 0) "pointer" else "not-allowed")
                            color(Color(colors.text))
                            fontSize(Typography.sm)
                        }
                    }) { Text("Previous") }

                    Span({
                        style {
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                        }
                    }) { Text("Page ${currentPage + 1} of $totalPages") }

                    Button({
                        onClick { if (currentPage < totalPages - 1) currentPage++ }
                        if (currentPage >= totalPages - 1) attr("disabled", "")
                        style {
                            padding(6.px, 12.px)
                            backgroundColor(Color(colors.surface))
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            cursor(if (currentPage < totalPages - 1) "pointer" else "not-allowed")
                            color(Color(colors.text))
                            fontSize(Typography.sm)
                        }
                    }) { Text("Next") }
                }
            }
        }
    }
}

@Composable
private fun Badge(text: String, textColor: String, bgColor: String) {
    Span({
        style {
            fontSize(11.px)
            fontWeight("500")
            padding(2.px, 8.px)
            borderRadius(10.px)
            color(Color(textColor))
            backgroundColor(Color(bgColor))
            property("white-space", "nowrap")
        }
    }) { Text(text) }
}

@Composable
private fun FilterPill(label: String, isActive: Boolean, onClick: () -> Unit) {
    val colors = AppTheme.colors
    Button({
        onClick { onClick() }
        style {
            padding(4.px, 12.px)
            fontSize(Typography.xs)
            fontWeight(if (isActive) "600" else "400")
            border(1.px, LineStyle.Solid, Color(if (isActive) colors.primary else colors.border))
            borderRadius(16.px)
            cursor("pointer")
            backgroundColor(Color(if (isActive) colors.primary else "transparent"))
            color(Color(if (isActive) "#ffffff" else colors.text))
            property("transition", "all 0.15s")
        }
    }) { Text(label) }
}
